import { useInterfaceMode } from "@/contexts/InterfaceModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * List of routes that require Admin Mode (advanced).
 * These are Platform Systems pages hidden from User Mode.
 */
const ADMIN_MODE_ROUTES = [
  "/command-center",
  "/ai-learning",
  "/activity-map",
  "/conversion-funnel",
  "/retention",
  "/session-replay",
  "/success-scores",
];

export function isAdminModeRoute(path: string): boolean {
  return ADMIN_MODE_ROUTES.some(route => path === route || path.startsWith(route + "/"));
}

/**
 * Route guard that blocks access to admin-only pages when not in Admin Mode.
 * SuperAdmin/PrimaryOwner always have access (they're always in Admin Mode).
 */
export function AdminModeGate({ children }: { children: React.ReactNode }) {
  const { isAdvanced } = useInterfaceMode();
  const { effectiveRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isStaff = effectiveRole === "primary_owner" || effectiveRole === "owner";

  // Staff always has access
  if (isStaff || isAdvanced) {
    return <>{children}</>;
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-4 max-w-md px-6">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center">
          <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {t("userSettings.adminModeOnly")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("userSettings.adminModeOnlyDesc")}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate("/user-settings")}
          className="gap-2"
        >
          <Settings className="h-4 w-4" />
          {t("userSettings.switchToAdminMode")}
        </Button>
      </div>
    </div>
  );
}
